/**
 * Tabs component implementation using custom elements
 * @description A tab system built with 3 custom elements:
 * TabsProvider - The main container that manages tab state and coordinates changes
 * TabTrigger - The clickable trigger element that activates its associated tab content
 * TabContent - The content container that is shown/hidden based on tab selection
 * @example
 * <tabs-provider>
 *   <tab-trigger handle="tab1"><button type="button">Tab 1</button></tab-trigger>
 *   <tab-content id="tab1">Content 1</tab-content>
 * </tabs-provider>
 */

/**
 * @class TabsProvider
 * @extends {HTMLElement}
 */
class TabsProvider extends HTMLElement {
  /** @type {HTMLElement[]} */
  tabTriggers = this.querySelectorAll('tab-trigger');

  /** @type {HTMLElement[]} */
  tabContents = this.querySelectorAll('tab-content');

  connectedCallback() {
    this.addEventListener('tab-change', (event) => this.handleChange(event));
  }

  /**
   * @function handleChange
   * @description Handle the tab change event.
   * @param {Event} event
   */
  handleChange(event) {
    const handle = event?.detail?.handle;

    // Get the current tab trigger and content
    const currentTabTrigger = this.querySelector(`tab-trigger[handle="${handle}"]`);
    const currentTabContent = this.querySelector(`tab-content#${handle}`);

    // Open the current tab trigger and close all other tab triggers
    this.tabTriggers.forEach((tabTrigger) => {
      tabTrigger === currentTabTrigger ? tabTrigger.open() : tabTrigger.close();
    });

    // Open the current tab content and close all other tab contents
    this.tabContents.forEach((tabContent) => {
      tabContent === currentTabContent ? tabContent.open() : tabContent.close();
    });
  }
}

customElements.define('tabs-provider', TabsProvider);

/**
 * @class TabTrigger
 * @extends {HTMLElement}
 */
class TabTrigger extends HTMLElement {
  /** @type {string} */
  handle = this.getAttribute('handle');

  /** @type {HTMLElement} */
  button = this.querySelector('button[type="button"]');

  connectedCallback() {
    this.button?.addEventListener('click', () => {
      this.dispatchEvent(
        new CustomEvent('tab-change', { bubbles: true, detail: { handle: this.handle } })
      );
    });
  }

  open() {
    this.button?.setAttribute('aria-expanded', 'true');
    this.button?.classList.add('open');
  }

  close() {
    this.button?.setAttribute('aria-expanded', 'false');
    this.button?.classList.remove('open');
  }
}

customElements.define('tab-trigger', TabTrigger);

/**
 * @class TabContent
 * @extends {HTMLElement}
 */
class TabContent extends HTMLElement {
  open() {
    this.classList.remove('hidden');
    this.classList.add('block');
  }

  close() {
    this.classList.add('hidden');
    this.classList.remove('block');
  }
}

customElements.define('tab-content', TabContent);
